/**
 * Shared free-text matcher for the Reconciliation page's Customer / Product
 * filters. The two filters live on the page and apply across every tab; each
 * panel feeds its own customer/product-bearing fields into {@link textMatches}.
 * An empty/blank query matches everything (the filter is off), so a row is
 * shown when it matches the customer filter AND the product filter.
 */
export function textMatches(query: string, ...parts: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return parts.some((p) => (p ?? '').toLowerCase().includes(q));
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TRAILING_PAREN_RE = /\(([^()]*)\)\s*$/;

/**
 * The per-subscription GUID Synnex embeds as a trailing "(...)" on a renewal
 * row's SKU (e.g. "Microsoft 365 Business Standard (55dc3656-…)"). Synnex leaves
 * the explicit subscription-ref column empty and carries the GUID here instead.
 * Returns null when there is no GUID-shaped trailing token.
 */
export function extractSubscriptionRef(sku?: string | null): string | null {
  if (!sku) return null;
  const tail = TRAILING_PAREN_RE.exec(sku)?.[1]?.trim();
  return tail && GUID_RE.test(tail) ? tail : null;
}

/**
 * Whether a renewal row belongs to a customer. Prefers the precise
 * per-subscription key — the row's explicit subscriptionRef or the GUID embedded
 * in its SKU, matched against the set of the customer's subscription refs — so it
 * still matches when Synnex lists the account under a different name than our
 * customer (e.g. a trust vs its trading name). Falls back to customer-name
 * containment for rows/customers without a usable ref.
 */
export function renewalRowMatchesCustomer(
  row: { subscriptionRef?: string | null; sku?: string | null; customerIdentifier?: string | null },
  customerName: string | null | undefined,
  customerSubscriptionRefs: Set<string>,
): boolean {
  const ref = (row.subscriptionRef ?? extractSubscriptionRef(row.sku))?.trim().toLowerCase();
  if (ref && customerSubscriptionRefs.has(ref)) return true;
  return !!customerName && textMatches(customerName, row.customerIdentifier);
}
